import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Search, Trash2, Plus, Lock } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { productionService } from '@services/productionService';
import { productService } from '@services/productService';
import { rawMaterialService } from '@services/rawMaterialService';
import { BomItemFormData, BOM_PARENT_MATERIAL_TYPE_LABELS } from '@/types/production';

type ParentType = 'product' | 'material';

interface ParentOption {
  id: number;
  code: string;
  description: string;
  typeLabel?: string;
}

interface ComponentRow {
  key: number;
  component_raw_material_id: number;
  code: string;
  description: string;
  unit?: string;
  quantity: string;
  scrap_percent: string;
  notes: string;
}

let rowKeySeq = 1;

const parseNumber = (value: string): number => {
  const parsed = parseFloat((value || '').replace(',', '.'));
  return isNaN(parsed) ? NaN : parsed;
};

/**
 * Formulário de Lista de Materiais (BOM) — criação e edição.
 * O pai da BOM é um produto de fabricação própria OU uma matéria-prima
 * composta/semiacabada. Os componentes são sempre matérias-primas.
 */
export const BomFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pai da BOM
  const [parentType, setParentType] = useState<ParentType>('product');
  const [selectedParent, setSelectedParent] = useState<ParentOption | null>(null);
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState<ParentOption[]>([]);
  const [parentSearching, setParentSearching] = useState(false);
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const parentBoxRef = useRef<HTMLDivElement>(null);

  // Dados gerais
  const [outputQuantity, setOutputQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Componentes
  const [items, setItems] = useState<ComponentRow[]>([]);
  const [componentSearch, setComponentSearch] = useState('');
  const [componentResults, setComponentResults] = useState<ParentOption[]>([]);
  const [componentSearching, setComponentSearching] = useState(false);
  const [showComponentDropdown, setShowComponentDropdown] = useState(false);
  const componentBoxRef = useRef<HTMLDivElement>(null);

  // ============================================================
  // Carregamento (edição)
  // ============================================================
  useEffect(() => {
    if (isEditing && id) {
      loadBom(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const loadBom = async (bomId: string) => {
    try {
      setLoading(true);
      setError(null);

      const bom = await productionService.getBillOfMaterial(parseInt(bomId));

      setParentType(bom.product_id ? 'product' : 'material');
      setSelectedParent({
        id: bom.product_id || bom.raw_material_id || 0,
        code: bom.parent_code || '',
        description: bom.parent_description || '',
      });
      setOutputQuantity(String(bom.output_quantity ?? 1));
      setNotes(bom.notes || '');
      setIsActive(bom.is_active !== false);
      setItems(
        (bom.items || []).map((item) => ({
          key: rowKeySeq++,
          component_raw_material_id: item.component_raw_material_id,
          code: item.component_code || String(item.component_raw_material_id),
          description: item.component_description || '',
          unit: item.component_unit,
          quantity: String(item.quantity ?? ''),
          scrap_percent: String(item.scrap_percent ?? 0),
          notes: item.notes || '',
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar lista de materiais');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Busca do pai (produto ou material composto/semiacabado)
  // ============================================================
  useEffect(() => {
    if (isEditing) return;

    const term = parentSearch.trim();
    if (term.length < 2) {
      setParentResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setParentSearching(true);

        if (parentType === 'product') {
          const { products } = await productService.searchProducts(term, 1, 20, {
            is_active: true,
          });
          const manufactured = (products || []).filter((p: any) => p.is_manufactured);
          setParentResults(
            manufactured.slice(0, 10).map((p: any) => ({
              id: p.id,
              code: p.sku,
              description: p.description,
            }))
          );
        } else {
          // Materiais compostos (2) e semiacabados (3)
          const [composite, semifinished] = await Promise.all([
            rawMaterialService.searchRawMaterials(term, 1, 10, { is_active: true, materialType: 2 }),
            rawMaterialService.searchRawMaterials(term, 1, 10, { is_active: true, materialType: 3 }),
          ]);
          const merged = [...composite.rawMaterials, ...semifinished.rawMaterials];
          setParentResults(
            merged.slice(0, 10).map((m) => ({
              id: m.id,
              code: m.code,
              description: m.description,
              typeLabel: BOM_PARENT_MATERIAL_TYPE_LABELS[m.material_type],
            }))
          );
        }
        setShowParentDropdown(true);
      } catch {
        setParentResults([]);
      } finally {
        setParentSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSearch, parentType]);

  // ============================================================
  // Busca de componentes (matérias-primas)
  // ============================================================
  useEffect(() => {
    const term = componentSearch.trim();
    if (term.length < 2) {
      setComponentResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setComponentSearching(true);
        const { rawMaterials } = await rawMaterialService.searchRawMaterials(term, 1, 10, {
          is_active: true,
        });
        setComponentResults(
          (rawMaterials || []).map((m) => ({
            id: m.id,
            code: m.code,
            description: m.description,
            typeLabel: m.unit,
          }))
        );
        setShowComponentDropdown(true);
      } catch {
        setComponentResults([]);
      } finally {
        setComponentSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [componentSearch]);

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (parentBoxRef.current && !parentBoxRef.current.contains(event.target as Node)) {
        setShowParentDropdown(false);
      }
      if (componentBoxRef.current && !componentBoxRef.current.contains(event.target as Node)) {
        setShowComponentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================
  // Manipulação de componentes
  // ============================================================
  const addComponent = (option: ParentOption) => {
    setShowComponentDropdown(false);
    setComponentSearch('');
    setComponentResults([]);

    if (items.some((row) => row.component_raw_material_id === option.id)) {
      setErrors((prev) => ({
        ...prev,
        items: `O componente "${option.code}" já foi adicionado à lista`,
      }));
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.items;
      return newErrors;
    });

    setItems((prev) => [
      ...prev,
      {
        key: rowKeySeq++,
        component_raw_material_id: option.id,
        code: option.code,
        description: option.description,
        unit: option.typeLabel,
        quantity: '1',
        scrap_percent: '0',
        notes: '',
      },
    ]);
  };

  const removeComponent = (key: number) => {
    setItems((prev) => prev.filter((row) => row.key !== key));
  };

  const updateComponent = (key: number, field: 'quantity' | 'scrap_percent' | 'notes', value: string) => {
    setItems((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  // ============================================================
  // Validação e salvamento
  // ============================================================
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEditing && !selectedParent) {
      newErrors.parent =
        parentType === 'product'
          ? 'Selecione o produto de fabricação própria'
          : 'Selecione o material composto/semiacabado';
    }

    const output = parseNumber(outputQuantity);
    if (isNaN(output) || output <= 0) {
      newErrors.output_quantity = 'Quantidade de saída deve ser maior que zero';
    }

    if (items.length === 0) {
      newErrors.items = 'A lista deve ter pelo menos 1 componente';
    } else {
      const seen = new Set<number>();
      for (const row of items) {
        if (seen.has(row.component_raw_material_id)) {
          newErrors.items = `Componente duplicado: "${row.code}"`;
          break;
        }
        seen.add(row.component_raw_material_id);

        const qty = parseNumber(row.quantity);
        if (isNaN(qty) || qty <= 0) {
          newErrors.items = `Quantidade do componente "${row.code}" deve ser maior que zero`;
          break;
        }

        const scrap = row.scrap_percent.trim() === '' ? 0 : parseNumber(row.scrap_percent);
        if (isNaN(scrap) || scrap < 0 || scrap > 100) {
          newErrors.items = `Perda (%) do componente "${row.code}" deve estar entre 0 e 100`;
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildItemsPayload = (): BomItemFormData[] =>
    items.map((row) => ({
      component_raw_material_id: row.component_raw_material_id,
      quantity: parseNumber(row.quantity),
      scrap_percent: row.scrap_percent.trim() === '' ? 0 : parseNumber(row.scrap_percent),
      notes: row.notes.trim() || undefined,
    }));

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        await productionService.updateBillOfMaterial(parseInt(id), {
          output_quantity: parseNumber(outputQuantity),
          notes: notes.trim() || undefined,
          is_active: isActive,
          items: buildItemsPayload(),
        });
      } else {
        await productionService.createBillOfMaterial({
          ...(parentType === 'product'
            ? { product_id: selectedParent!.id }
            : { raw_material_id: selectedParent!.id }),
          output_quantity: parseNumber(outputQuantity),
          notes: notes.trim() || undefined,
          items: buildItemsPayload(),
        });
      }

      navigate('/production/bom');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar lista de materiais');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/bom')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/production/bom')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Lista de Materiais' : 'Nova Lista de Materiais'}
          </h1>
          <p className="text-slate-600">
            {isEditing
              ? 'Altere a composição de materiais do item'
              : 'Defina o item a ser produzido e seus componentes'}
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        {/* Item produzido (pai) */}
        <Card>
          <CardHeader
            title="Item Produzido"
            subtitle={
              isEditing
                ? 'O item da lista de materiais não pode ser alterado'
                : 'Escolha o tipo e busque o item que será produzido'
            }
          />
          <CardContent>
            {isEditing ? (
              <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <Lock size={18} className="text-slate-400 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                      {selectedParent?.code}
                    </code>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        parentType === 'product'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {parentType === 'product' ? 'Produto' : 'Material'}
                    </span>
                  </div>
                  <p className="font-medium text-slate-900 mt-1">
                    {selectedParent?.description}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Tipo do pai */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tipo de item *
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="parentType"
                        checked={parentType === 'product'}
                        onChange={() => {
                          setParentType('product');
                          setSelectedParent(null);
                          setParentSearch('');
                          setParentResults([]);
                        }}
                        className="border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm">Produto de fabricação própria</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="parentType"
                        checked={parentType === 'material'}
                        onChange={() => {
                          setParentType('material');
                          setSelectedParent(null);
                          setParentSearch('');
                          setParentResults([]);
                        }}
                        className="border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm">Material composto/semiacabado</span>
                    </label>
                  </div>
                </div>

                {/* Autocomplete do pai */}
                {selectedParent ? (
                  <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="bg-white px-2 py-1 rounded text-xs">
                          {selectedParent.code}
                        </code>
                        {selectedParent.typeLabel && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {selectedParent.typeLabel}
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-slate-900 mt-1">
                        {selectedParent.description}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedParent(null);
                        setParentSearch('');
                      }}
                    >
                      Trocar
                    </Button>
                  </div>
                ) : (
                  <div className="relative" ref={parentBoxRef}>
                    <Input
                      label={
                        parentType === 'product'
                          ? 'Buscar produto de fabricação própria *'
                          : 'Buscar material composto/semiacabado *'
                      }
                      placeholder="Digite código ou descrição (mín. 2 caracteres)..."
                      value={parentSearch}
                      onChange={(e) => setParentSearch(e.target.value)}
                      onFocus={() => parentResults.length > 0 && setShowParentDropdown(true)}
                      startIcon={<Search size={20} />}
                      error={errors.parent}
                    />
                    {parentSearching && (
                      <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                        <span className="animate-spin w-3 h-3 border border-blue-500 border-t-transparent rounded-full inline-block" />
                        Buscando...
                      </p>
                    )}
                    {showParentDropdown && parentResults.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                        {parentResults.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                            onClick={() => {
                              setSelectedParent(option);
                              setShowParentDropdown(false);
                              setErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.parent;
                                return newErrors;
                              });
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                                {option.code}
                              </code>
                              {option.typeLabel && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  {option.typeLabel}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-700 mt-0.5">{option.description}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {showParentDropdown &&
                      !parentSearching &&
                      parentSearch.trim().length >= 2 &&
                      parentResults.length === 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 px-4 py-3 text-sm text-slate-500">
                          Nenhum item encontrado
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dados gerais */}
        <Card>
          <CardHeader title="Dados Gerais" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Quantidade de Saída *"
                type="number"
                placeholder="1"
                value={outputQuantity}
                onChange={(e) => setOutputQuantity(e.target.value)}
                error={errors.output_quantity}
                step={1}
                min={1}
                helperText="Quantidade produzida por execução da lista de materiais"
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Observações / Notas
                </label>
                <textarea
                  className="input w-full resize-y"
                  placeholder="Informações adicionais sobre a lista de materiais..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>

              {isEditing && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <label htmlFor="is_active" className="text-sm text-slate-700">
                    Lista de materiais ativa
                  </label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Componentes */}
        <Card>
          <CardHeader
            title="Componentes"
            subtitle="Matérias-primas que compõem o item produzido (mínimo 1)"
          />
          <CardContent>
            <div className="space-y-4">
              {/* Autocomplete de componente */}
              <div className="relative" ref={componentBoxRef}>
                <Input
                  label="Adicionar componente"
                  placeholder="Buscar matéria-prima por código ou descrição (mín. 2 caracteres)..."
                  value={componentSearch}
                  onChange={(e) => setComponentSearch(e.target.value)}
                  onFocus={() => componentResults.length > 0 && setShowComponentDropdown(true)}
                  startIcon={<Plus size={20} />}
                />
                {componentSearching && (
                  <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                    <span className="animate-spin w-3 h-3 border border-blue-500 border-t-transparent rounded-full inline-block" />
                    Buscando...
                  </p>
                )}
                {showComponentDropdown && componentResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    {componentResults.map((option) => {
                      const alreadyAdded = items.some(
                        (row) => row.component_raw_material_id === option.id
                      );
                      return (
                        <button
                          key={option.id}
                          type="button"
                          disabled={alreadyAdded}
                          className="w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => addComponent(option)}
                        >
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                              {option.code}
                            </code>
                            {alreadyAdded && (
                              <span className="text-xs text-amber-600 font-medium">
                                Já adicionado
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-0.5">{option.description}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
                {showComponentDropdown &&
                  !componentSearching &&
                  componentSearch.trim().length >= 2 &&
                  componentResults.length === 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 px-4 py-3 text-sm text-slate-500">
                      Nenhuma matéria-prima encontrada
                    </div>
                  )}
              </div>

              {errors.items && <p className="text-sm text-red-600">{errors.items}</p>}

              {/* Tabela de componentes */}
              {items.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-slate-300 rounded-lg">
                  <p className="text-slate-500 text-sm">
                    Nenhum componente adicionado. Use a busca acima para incluir matérias-primas.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <th className="text-left">Código</th>
                      <th className="text-left">Descrição</th>
                      <th className="text-center">Unidade</th>
                      <th className="text-right">Quantidade</th>
                      <th className="text-right">Perda (%)</th>
                      <th className="text-left">Observações</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </TableHeader>
                  <tbody>
                    {items.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>
                          <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                            {row.code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-900">{row.description}</p>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm text-slate-600">{row.unit || '-'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <input
                            type="number"
                            className="border rounded px-2 py-1 w-24 text-right"
                            value={row.quantity}
                            min={0}
                            step={0.001}
                            onChange={(e) => updateComponent(row.key, 'quantity', e.target.value)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <input
                            type="number"
                            className="border rounded px-2 py-1 w-20 text-right"
                            value={row.scrap_percent}
                            min={0}
                            max={100}
                            step={0.1}
                            onChange={(e) =>
                              updateComponent(row.key, 'scrap_percent', e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            type="text"
                            className="border rounded px-2 py-1 w-full"
                            placeholder="Observações..."
                            value={row.notes}
                            onChange={(e) => updateComponent(row.key, 'notes', e.target.value)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            type="button"
                            onClick={() => removeComponent(row.key)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Remover componente"
                          >
                            <Trash2 size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/production/bom')} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} icon={<Save size={20} />}>
            {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Lista de Materiais'}
          </Button>
        </div>
      </div>
    </div>
  );
};
